import { Flex, Modal, Typography } from 'antd';
import { CloseOutlined, WarningOutlined } from '@ant-design/icons';
import { CustomButton } from '@/components/ui';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { openModal } from '@/slice/modal';

interface LateCancelPolicyProps {
  open: boolean;
  onCancel: () => void;
  bookingId: string;
  consultantId: string;
  fee: string;
  rescheduleClosed?: boolean;
}

// Franchisor lands here when cancelling a FUTURE meeting that starts in
// less than 24 hours. Offers the two paths forward: reschedule (fee follows
// the meeting to its new date) or confirm the late cancellation (fee is
// charged now).
export const LateCancelPolicy = (props: LateCancelPolicyProps) => {
  const dispatch = useAppDispatch();
  const { bookingId, consultantId, rescheduleClosed } = props;
  // Guard against callers that forgot to pass `fee` — never render literal
  // "undefined" in the copy below.
  const fee = props.fee ?? '$0';

  // Carry `rescheduleClosed` forward. LATE_CANCEL_CONFIRM can navigate BACK
  // here, and it rebuilds this modal from the props it was given — so dropping
  // the flag on the way out meant the rebuilt modal defaulted to "open" and
  // offered RESCHEDULE MEETING again for a cycle the backend rejects.
  const forward = (modalType: string) =>
    dispatch(
      openModal({
        modalType,
        modalProps: { bookingId, consultantId, fee, rescheduleClosed },
      }),
    );

  return (
    <Modal
      open={props.open}
      onCancel={props.onCancel}
      width={640}
      centered
      footer={null}
      closeIcon={<CloseOutlined style={{ color: '#000' }} />}
      styles={{ content: { borderRadius: 20 }, body: { padding: '18px 18px' } }}
    >
      <Flex vertical align={'center'} gap={16}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: 'rgba(246, 181, 30, 0.14)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <WarningOutlined style={{ fontSize: 24, color: '#9a6b00' }} />
        </div>
        <Typography.Text
          style={{ fontSize: 22, fontWeight: 700, textAlign: 'center' }}
        >
          This meeting starts in less than 24 hours
        </Typography.Text>
        <Typography.Text type={'secondary'} style={{ textAlign: 'center' }}>
          {props.rescheduleClosed
            ? `At least 24 hours' notice is required to cancel. Cancelling now will incur a ${fee} meeting fee.`
            : `At least 24 hours' notice is required to cancel. Cancelling now will incur a ${fee} meeting fee. We recommend requesting a reschedule instead. If the consultant approves your request, the meeting fee is assigned to the new meeting date.`}
        </Typography.Text>
      </Flex>
      <Flex vertical align={'center'} gap={12} style={{ marginTop: 24 }}>
        {!props.rescheduleClosed && (
          <CustomButton
            type={'primary'}
            text={'RESCHEDULE MEETING'}
            onClick={() => forward('RESCHEDULE_BOOKING')}
          />
        )}
        <CustomButton
          type={'text'}
          text={`Cancel anyway & pay ${fee}`}
          onClick={() => forward('LATE_CANCEL_CONFIRM')}
          style={{
            color: '#DC2626',
            fontSize: 14,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
            textAlign: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            boxShadow: 'none',
            // Same box height as a real button (CustomButton's .primary/
            // .default are 48px) rather than 'auto' — this is an invisible
            // button, not a text link, so the gap above it should read as
            // button-to-button spacing, not text-to-button.
            height: 48,
          }}
        />
      </Flex>
    </Modal>
  );
};
